import './ComunidadFORVET.css'
import { useContext, useEffect, useState } from 'react';
import {
  Button, Badge, Image, AspectRatio, Skeleton,
  Loader, Modal, Divider
} from '@mantine/core';
import {
  IconDownload, IconUsers, IconFileText, IconExternalLink,
  IconPackage, IconArchive, IconInfoCircle, IconEye,
  IconCertificate, IconX, IconBrandWhatsapp, IconHelpCircle, IconBolt
} from '@tabler/icons-react';
import MainLayout from '../../layouts/MainLayout/MainLayout';
import AuthContext from '../../contexts/AuthContext';
import axios from 'axios';
import showNotification from '../../utils/showNotification';
import { ERROR_HANDLER } from '../../utils/error-handler';


const TRACK_ACTIONS = {
  view: 'views',
  download: 'downloads',
  link: 'external_clicks',
  tutorial: 'tutorial_clicks'
}

const getResourceType = (url) => {
  if (!url) return null;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
  const isVideoExtension = videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
  const isExternalVideo = url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
  if (isVideoExtension || isExternalVideo) return 'video';
  return 'image';
};

const getIconByCategory = category => {
  if (category === 'KIT DIGITAL') return <IconPackage size={18} />
  if (category === 'PAPERS') return <IconFileText size={18} />
  if (category === 'PLANTILLAS') return <IconArchive size={18} />
  if (category === 'TUTORIAL') return <IconCertificate size={18} />
  return <IconInfoCircle size={18} />
}


const ComunidadFORVET = () => {
  const { authToken } = useContext(AuthContext);
  const [resourcesByCategory, setResourcesByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  const trackAction = async (resourceId, action) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_SERVER}campus/community-resources/${resourceId}/track`;
      await axios.patch(url, { action }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
    } catch (error) {
      console.log('Error al registrar acción:', error);
    }
  };

  const handleDownload = async (e, url, resourceId) => {
    e.preventDefault();
    trackAction(resourceId, TRACK_ACTIONS.download);

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = url.split('/').pop().split('?')[0] || 'descarga';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    const getCommunityResources = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_SERVER}campus/community-resources`;
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        };
        const { data } = await axios.get(url, config);
        setResourcesByCategory(data);
      } catch (error) {
        showNotification({
          color: "red",
          title: "Error al cargar recursos",
          message: ERROR_HANDLER[error.response?.status] || ERROR_HANDLER.defaultError
        });
      } finally {
        setLoading(false);
      }
    };
    getCommunityResources();
  }, [authToken]);

  const [isDetailModal, setIsDetailModal] = useState(null)

  return (
    <MainLayout>
      <section className="comunidad-layout">
        <div>
          <h1 className="comunidad-title">Comunidad FORVET</h1>
          <p className="comunidad-subtitle">
            Accedé a todas las herramientas, tutoriales y materiales descargables
            para potenciar tu práctica veterinaria.
          </p>
        </div>

        {loading && <Skeleton h={100} animate />}

        {(!loading && resourcesByCategory && Object.entries(resourcesByCategory)?.length > 1) && (
          <div className="comunidad-quicknav">
            <div className="comunidad-quicknav__icon">
              <IconBolt size={20} stroke={1.5} />
            </div>
            <div className="comunidad-quicknav__links">
              {Object.entries(resourcesByCategory).map(([category]) => (
                <Button
                  key={category}
                  variant="subtle"
                  color="var(--color-secondary)"
                  size="sm"
                  leftSection={getIconByCategory(category.toUpperCase())}
                  onClick={(e) => {
                    e.preventDefault();
                    const id = category.replaceAll(' ', '');
                    const element = document.getElementById(id);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  styles={{ label: { textDecoration: 'underline' } }}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="comunidad-whatsapp">
          <div className="comunidad-whatsapp__info">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <IconUsers size={22} color="var(--color-text-secondary)" />
              <span className="comunidad-whatsapp__title">Sumate al grupo de WhatsApp</span>
            </div>
            <p className="comunidad-whatsapp__text">
              Una comunidad donde las <strong>preguntas</strong> abren conversaciones y la <strong>experiencia</strong> de cada uno suma al <strong>aprendizaje colectivo</strong>.
            </p>
          </div>
          <Button
            color="teal"
            component="a"
            href="https://link.forvet.org/sumate-a-la-comunidad-cp"
            target="_blank"
            rightSection={<IconBrandWhatsapp size={18} />}
          >
            Unirme ahora
          </Button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Loader size="xl" variant="dots" />
          </div>
        ) : (
          Object.entries(resourcesByCategory).map(([category, items]) => (
            <div key={category} className="comunidad-category" id={category.replaceAll(' ', '')}>
              <div className="comunidad-category__header">
                <div className="comunidad-category__icon">
                  {getIconByCategory(category.toUpperCase().trim())}
                </div>
                <h2 className="comunidad-category__title">{category.toUpperCase()}</h2>
              </div>

              <div className="comunidad-grid">
                {items.map((item) => (
                  <div key={item._id} className="comunidad-card">
                    <div className="comunidad-card__image-section">
                      {item.type === 'video' && item.external_url ? (
                        <AspectRatio ratio={16 / 9}>
                          <iframe
                            src={item.external_url}
                            title={item.title}
                            style={{ border: 0 }}
                            allowFullScreen
                          />
                        </AspectRatio>
                      ) : (
                        <img
                          className="comunidad-card__image"
                          src={item.preview_url || 'https://placehold.co/600x400/0ca678/white?text=FORVET'}
                          alt={item.title}
                        />
                      )}
                    </div>

                    <div className="comunidad-card__quick-actions">
                      <Button
                        size="xs"
                        variant="light"
                        color="var(--color-secondary-darkest)"
                        leftSection={<IconEye size={16} />}
                        onClick={() => {
                          setIsDetailModal(item)
                          trackAction(item._id, TRACK_ACTIONS.view)
                        }}
                      >
                        Ver más
                      </Button>
                      {item.tutorial_url && (
                        <Button
                          size="xs"
                          variant="light"
                          color="var(--color-primary-light)"
                          leftSection={<IconHelpCircle size={16} />}
                          component="a"
                          href={item.tutorial_url}
                          target="_blank"
                          onClick={() => trackAction(item._id, TRACK_ACTIONS.tutorial)}
                        >
                          Tutorial
                        </Button>
                      )}
                    </div>

                    <div className="comunidad-card__body">
                      <div>
                        <div className="comunidad-card__meta">
                          <Badge color="var(--color-tertiary)" variant="dot" size="sm">{item.type}</Badge>
                          <div className="comunidad-card__tags">
                            {item.tags?.map(tag => (
                              <Badge key={tag} color="var(--color-tertiary)" variant="outline" size="xs">#{tag}</Badge>
                            ))}
                          </div>
                        </div>
                        <p className="comunidad-card__title">{item.title}</p>
                        <p className="comunidad-card__description">
                          {item.description || "Sin descripción disponible."}
                        </p>
                      </div>

                      <div className="comunidad-card__actions">
                        {item.external_url && (
                          <Button
                            component="a"
                            href={item.external_url}
                            target="_blank"
                            fullWidth
                            variant="light"
                            leftSection={<IconExternalLink size={18} />}
                            onClick={() => trackAction(item._id, TRACK_ACTIONS.link)}
                          >
                            Abrir Enlace
                          </Button>
                        )}
                        {item.download_url && (
                          <Button
                            fullWidth
                            variant="light"
                            color="var(--color-tertiary)"
                            leftSection={<IconDownload size={18} />}
                            onClick={(e) => handleDownload(e, item.download_url, item._id)}
                          >
                            Descargar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <DetailsModal opened={isDetailModal} onClose={() => setIsDetailModal(null)} trackAction={trackAction} handleDownload={handleDownload} />
    </MainLayout>
  );
};

const DetailsModal = ({ opened, onClose, trackAction, handleDownload }) => {
  if (!opened) return null;

  const data = opened;

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      size="xl"
      radius="md"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Button
          w="max-content"
          onClick={onClose}
          variant="outline"
          color="red"
          leftSection={<IconX size={18} />}
        >
          Cerrar
        </Button>

        <div className="comunidad-modal__header">
          {data.preview_url && (
            <img
              className="comunidad-modal__preview"
              src={data.preview_url}
              alt="portada"
            />
          )}
          <div className="comunidad-modal__info">
            <p className="comunidad-modal__title">{data.title}</p>
            <p className="comunidad-modal__description">
              {data.description || "Sin descripción disponible."}
            </p>
            <div className="comunidad-modal__meta">
              <Badge color="var(--color-tertiary)" variant="dot" size="md">{data.type}</Badge>
              <div className="comunidad-modal__tags">
                {data.tags?.map(tag => (
                  <Badge variant="outline" color="var(--color-tertiary)" key={tag} size="md" fw={500}>#{tag}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Divider />

        {data?.images && data?.images?.length > 0 ? (
          <div className="comunidad-modal__gallery">
            {data.images.map((image, index) => (
              <img
                key={index}
                className="comunidad-modal__gallery-image"
                src={image}
                alt={`${data.title} - ${index}`}
              />
            ))}
          </div>
        ) : data.type === 'video' && data.external_url ? (
          <AspectRatio ratio={16 / 9}>
            <iframe
              src={data.external_url}
              title={data.title}
              style={{ border: 0 }}
              allowFullScreen
            />
          </AspectRatio>
        ) : null}

        {data?.tutorial_url && (
          <>
            <Divider />
            <div className="comunidad-modal__tutorial">
              <div className="comunidad-modal__tutorial-label">
                <IconHelpCircle size={20} />
                <span>¿Cómo usarlo?</span>
              </div>
              {getResourceType(data.tutorial_url) === 'video' ? (
                <AspectRatio ratio={16 / 9}>
                  <iframe
                    src={data.tutorial_url.replace("watch?v=", "embed/")}
                    title="Tutorial Video"
                    style={{ border: 0, borderRadius: '8px' }}
                    allowFullScreen
                  />
                </AspectRatio>
              ) : (
                <Image
                  src={data.tutorial_url}
                  alt="Tutorial"
                  radius="md"
                  fallbackSrc="https://placehold.co/600x400?text=Error+al+cargar+tutorial"
                />
              )}
            </div>
          </>
        )}

        <div className="comunidad-modal__actions">
          {data?.external_url && (
            <Button
              component="a"
              href={data.external_url}
              target="_blank"
              fullWidth
              variant="outline"
              onClick={() => trackAction(data._id, TRACK_ACTIONS.link)}
              leftSection={<IconExternalLink size={18} />}
            >
              Abrir Enlace
            </Button>
          )}
          {data?.download_url && (
            <Button
              component="a"
              href={data.download_url}
              download
              target="_blank"
              fullWidth
              variant="filled"
              color="var(--color-tertiary)"
              onClick={(e) => handleDownload(e, data.download_url, data._id)}
              leftSection={<IconDownload size={18} />}
            >
              Descargar
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ComunidadFORVET;
